import { useEffect, useMemo, useState } from "react";
import type { RoleListItem } from "../types.tsx";
import { StandardModal } from "../table-page/StandardModal.tsx";

export type RoleFormValues = {
  role: string;
  description: string;
};

const empty: RoleFormValues = {
  role: "",
  description: "",
};

function fromRole(role: RoleListItem): RoleFormValues {
  return {
    role: role.role,
    description: role.description ?? "",
  };
}

export type RoleFormModalProps = {
  open: boolean;
  mode: "create" | "edit";
  role?: RoleListItem | null;
  loading?: boolean;
  serverError?: string | null;
  fieldErrors?: Partial<Record<keyof RoleFormValues | "form", string>>;
  onClose: () => void;
  onSubmit: (values: RoleFormValues) => void;
};

export function RoleFormModal({
  open,
  mode,
  role,
  loading,
  serverError,
  fieldErrors,
  onClose,
  onSubmit,
}: RoleFormModalProps) {
  const [values, setValues] = useState<RoleFormValues>(empty);
  const [initial, setInitial] = useState<RoleFormValues>(empty);

  useEffect(() => {
    if (!open) return;
    const next = mode === "edit" && role ? fromRole(role) : empty;
    setValues(next);
    setInitial(next);
  }, [open, mode, role]);

  const dirty = values.role !== initial.role || values.description !== initial.description;
  const title = mode === "create" ? "Add Role" : "Edit Role";
  const primaryLabel = mode === "create" ? "Save" : "Update";

  const requestClose = () => {
    if (loading) return;
    if (dirty && !window.confirm("You have unsaved changes. Are you sure you want to discard them?")) return;
    onClose();
  };

  const footer = useMemo(
    () => (
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="ds-btn ds-btn--ghost" onClick={requestClose} disabled={loading}>
          Cancel
        </button>
        <button
          type="button"
          className="ds-btn ds-btn--primary"
          disabled={loading}
          onClick={() => onSubmit(values)}
        >
          {primaryLabel}
        </button>
      </div>
    ),
    [loading, onSubmit, primaryLabel, requestClose, values],
  );

  const err = (k: keyof RoleFormValues | "form") => fieldErrors?.[k];

  return (
    <StandardModal open={open} title={title} onClose={requestClose} footer={footer} loading={loading}>
      <div className="ds-stack ds-stack--loose" style={{ width: "100%" }}>
        {serverError || err("form") ? (
          <p className="ds-error-inline" style={{ margin: 0 }}>
            {serverError ?? err("form")}
          </p>
        ) : null}

        <label className="ds-field">
          <span className="ds-field__label">Role</span>
          <input
            className="ds-input"
            placeholder="Enter Role"
            value={values.role}
            onChange={(e) => setValues((v) => ({ ...v, role: e.target.value }))}
            autoComplete="off"
          />
          {err("role") ? <span className="ds-field__error">{err("role")}</span> : null}
        </label>

        <label className="ds-field">
          <span className="ds-field__label">Description</span>
          <textarea
            className="ds-input"
            placeholder="Enter Description"
            rows={4}
            value={values.description}
            onChange={(e) => setValues((v) => ({ ...v, description: e.target.value }))}
          />
          {err("description") ? <span className="ds-field__error">{err("description")}</span> : null}
        </label>
      </div>
    </StandardModal>
  );
}
